import { useCallback, useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

import styles from './Chip.module.css';
import focus from './focus.module.css';

export interface ChipOption<TValue extends string> {
  readonly value: TValue;
  readonly label: ReactNode;
  readonly disabled?: boolean;
}

export interface ChipGroupProps<TValue extends string> {
  /** Names the group for a screen reader. Required: an unlabelled group of chips is a row of noise. */
  readonly label: string;
  readonly options: readonly ChipOption<TValue>[];
  readonly value: TValue | null;
  readonly onChange: (value: TValue) => void;
  readonly className?: string;
}

/**
 * A row of choices — category, kind, date range (`LED-04`, `TRK-02`).
 *
 * These are radio buttons wearing different clothes, so they behave like radio buttons: one tab stop for
 * the whole group, arrow keys move between options and select as they go, Home and End jump to the ends.
 * That is what a screen-reader user and a keyboard user both expect, and it is the reason this is a group
 * component rather than a loose collection of buttons - the behaviour lives between the chips, not in them.
 */
export function ChipGroup<TValue extends string>({
  label,
  options,
  value,
  onChange,
  className,
}: ChipGroupProps<TValue>): React.JSX.Element {
  const refs = useRef(new Map<TValue, HTMLButtonElement | null>());

  const move = useCallback(
    (from: number, step: number) => {
      const selectable = options.filter((option) => !option.disabled);
      if (selectable.length === 0) {
        return;
      }

      const current = selectable.findIndex((option) => option.value === options[from]?.value);
      const next = selectable[(current + step + selectable.length) % selectable.length];
      if (!next) {
        return;
      }

      onChange(next.value);
      refs.current.get(next.value)?.focus();
    },
    [onChange, options],
  );

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    const step =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;

    if (step !== 0) {
      event.preventDefault();
      move(index, step);
      return;
    }

    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const selectable = options.filter((option) => !option.disabled);
      const target = event.key === 'Home' ? selectable[0] : selectable[selectable.length - 1];
      if (target) {
        onChange(target.value);
        refs.current.get(target.value)?.focus();
      }
    }
  };

  // One tab stop for the group: the selected chip, or the first one when nothing is selected yet.
  const tabStop = value ?? options.find((option) => !option.disabled)?.value ?? null;

  return (
    <div role="radiogroup" aria-label={label} className={[styles.group, className ?? ''].filter(Boolean).join(' ')}>
      {options.map((option, index) => (
        <button
          key={option.value}
          ref={(element) => {
            refs.current.set(option.value, element);
          }}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          tabIndex={option.value === tabStop ? 0 : -1}
          disabled={option.disabled ?? false}
          className={[styles.chip, focus.focusable, value === option.value ? styles.selected : '']
            .filter(Boolean)
            .join(' ')}
          onClick={() => {
            onChange(option.value);
          }}
          onKeyDown={(event) => {
            onKeyDown(event, index);
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
